import { useEffect, useMemo, useState } from "react";
import { useContent } from "@/context/ContentContext";
import { api, originUrl } from "@/lib/api";
import { toast } from "sonner";
import { Users, Check, Minus, Plus } from "lucide-react";

export default function PatchHatBar() {
  const pc = useContent().pages.patch_hat_bar;
  const [cfg, setCfg] = useState(null);
  const [guests, setGuests] = useState(6);
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", event_time: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/patch-hat/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => { if (cfg?.min_guests) setGuests((g) => Math.max(g, cfg.min_guests)); }, [cfg]);

  const price = useMemo(() => {
    if (!cfg) return { ppp: 0, total: 0 };
    const tier = (cfg.tiers || []).find((t) => guests >= t.min_guests && guests <= t.max_guests);
    const ppp = tier?.price_per_person || 0;
    return { ppp, total: ppp * guests };
  }, [cfg, guests]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/patch-hat/bookings", { ...form, guests: Number(guests), origin_url: originUrl() });
      setDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="ph-pending">
      <div className="bg-white rounded-3xl p-8 shadow-card">
        <h1 className="font-serif text-4xl">Request received!</h1>
        <p className="mt-4 text-charcoal/75">Your Patch Hat Bar for <b>{guests} guests</b> is <b>pending payment</b>. Total due: <b>${Number(done.amount).toFixed(2)}</b>.</p>
        {done.square_link ? <a href={done.square_link} target="_blank" rel="noreferrer" data-testid="ph-pay-square" className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-cream px-8 py-4 font-bold shadow-button">Pay with Square</a>
          : <p className="mt-6 text-sm text-charcoal-muted">We'll email you a secure Square payment link shortly.</p>}
      </div>
    </div>
  );

  if (!cfg) return <div className="max-w-4xl mx-auto px-6 py-16 text-center">Loading…</div>;
  const belowMin = guests < (cfg.min_guests || 5);

  return (
    <div>
      <section className="relative overflow-hidden bg-charcoal text-cream py-20">
        <div className="splat splat-pink w-56 h-56 -top-16 -left-16 opacity-30"></div>
        <div className="splat splat-cyan w-48 h-48 -bottom-16 -right-16 opacity-30"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center relative z-10">
          <div>
            <div className="text-terracotta-light text-sm tracking-widest uppercase font-bold">{pc.eyebrow}</div>
            <h1 className="mt-2 font-serif text-5xl md:text-6xl leading-none">{pc.heading} <span className="text-forest-light">{pc.heading_accent}</span></h1>
            <p className="mt-6 text-cream/85 text-lg">{cfg.description}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {(cfg.tiers || []).map((t, i) => (
                <div key={i} className="rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm font-bold">
                  {t.min_guests}{t.max_guests < 999 ? `–${t.max_guests}` : "+"} guests · ${t.price_per_person}/person
                </div>
              ))}
            </div>
          </div>
          {cfg.hero_photo && <img src={cfg.hero_photo} alt="Patch Hat Bar" className="rounded-3xl w-full h-80 object-cover border-4 border-white shadow-hover"/>}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div className="bg-white rounded-3xl p-8 shadow-card">
            <h2 className="font-serif text-3xl">{pc.included_heading}</h2>
            <ul className="mt-6 space-y-3">
              {(cfg.included_items || []).map((it, i) => (
                <li key={i} className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-terracotta text-white grid place-items-center"><Check size={16}/></div><span className="font-medium">{it}</span></li>
              ))}
            </ul>
          </div>
          <form onSubmit={submit} className="bg-cream-alt rounded-3xl p-8" data-testid="patch-hat-form">
            <h2 className="font-serif text-3xl">{pc.form_heading}</h2>
            <div className="mt-6">
              <label className="text-sm font-bold uppercase tracking-widest text-charcoal-muted">Guests</label>
              <div className="mt-3 flex items-center gap-4 justify-center">
                <button type="button" onClick={() => setGuests(Math.max(1, guests - 1))} className="w-12 h-12 rounded-full bg-white text-2xl font-bold" data-testid="ph-minus"><Minus size={20} className="mx-auto"/></button>
                <input data-testid="ph-guests" type="number" min="1" value={guests} onChange={(e) => setGuests(Math.max(1, Number(e.target.value) || 1))} className="w-24 text-center text-4xl font-serif rounded-2xl border-2 border-white py-3 focus:border-terracotta focus:outline-none"/>
                <button type="button" onClick={() => setGuests(guests + 1)} className="w-12 h-12 rounded-full bg-white text-2xl font-bold" data-testid="ph-plus"><Plus size={20} className="mx-auto"/></button>
              </div>
            </div>

            {belowMin ? (
              <div className="mt-6 rounded-2xl bg-white p-5 border-2 border-ochre" data-testid="ph-below-min">
                <p className="text-sm text-charcoal/85">{cfg.below_min_message}</p>
                {cfg.individual_available && (
                  <div className="mt-4 rounded-2xl bg-cream-alt p-4">
                    <div className="font-serif text-xl">Custom Patch Hat</div>
                    <p className="mt-1 text-sm text-charcoal-muted">{cfg.individual_description}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="font-serif text-2xl text-terracotta">${cfg.individual_price}</div>
                      <a href="/contact" className="rounded-full bg-terracotta text-white px-5 py-2 font-bold text-sm">Request one</a>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="mt-6 rounded-2xl bg-white p-5 flex items-center justify-between" data-testid="ph-total">
                  <div>
                    <div className="text-xs text-charcoal-muted">Price per guest</div>
                    <div className="font-serif text-2xl">${price.ppp}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-charcoal-muted">Total</div>
                    <div className="font-serif text-3xl text-terracotta">${price.total.toFixed(2)}</div>
                  </div>
                </div>
                <div className="mt-6 grid gap-4">
                  <F label="Full Name *" v={form.name} on={(v) => setForm({...form, name: v})} req name="name"/>
                  <F label="Email *" v={form.email} on={(v) => setForm({...form, email: v})} type="email" req name="email"/>
                  <F label="Phone" v={form.phone} on={(v) => setForm({...form, phone: v})} name="phone"/>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Preferred Date" v={form.event_date} on={(v) => setForm({...form, event_date: v})} type="date" name="date"/>
                    <F label="Time" v={form.event_time} on={(v) => setForm({...form, event_time: v})} type="time" name="time"/>
                  </div>
                  <div>
                    <label className="text-sm font-semibold">Notes</label>
                    <textarea data-testid="ph-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none bg-white"/>
                  </div>
                  <button data-testid="ph-submit" type="submit" disabled={busy} className="rounded-full bg-terracotta text-white px-8 py-4 font-bold shadow-button disabled:opacity-60">
                    {busy ? "Submitting…" : `Continue — $${price.total.toFixed(2)}`}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}

function F({ label, v, on, type = "text", req, name }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={`ph-${name}`} required={req} type={type} value={v} onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none bg-white"/>
    </div>
  );
}
